import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ReservationsEnhanced.module.scss";
import { dateDiffInNights } from "../utils/dateDiff";
import { API_ENDPOINTS, getAuthHeaders } from "../config/api";

function ReservationsEnhanced() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal control
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [activeReservation, setActiveReservation] = useState(null);
  const [previewContent, setPreviewContent] = useState({ subject: "", html: "", language: "" });

  // Quote form fields
  const [currency, setCurrency] = useState("TND");
  const [roomPrices, setRoomPrices] = useState({});
  const [totalCost, setTotalCost] = useState(0);
  const [language, setLanguage] = useState("fr");
  const [adminNotes, setAdminNotes] = useState("");
  
  // Confirmation/Unavailable language
  const [confirmLanguage, setConfirmLanguage] = useState("fr");
  const [unavailableLanguage, setUnavailableLanguage] = useState("fr");
  const [unavailableNotes, setUnavailableNotes] = useState("");

  useEffect(() => {
    fetchReservations();
  }, [filterStatus]);

  const fetchReservations = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filterStatus === "all" 
        ? API_ENDPOINTS.RESERVATIONS 
        : `${API_ENDPOINTS.RESERVATIONS}?status=${filterStatus}`;
      
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Échec du chargement des réservations");
      const data = await res.json();
      setReservations(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "Échec du chargement des réservations");
    } finally {
      setLoading(false);
    }
  };

  const openQuoteModal = (reservation) => {
    setActiveReservation(reservation);
    setShowQuoteModal(true);
    setCurrency(reservation.currency || "TND");
    setLanguage(reservation.preferredLanguage || "fr");
    
    // Initialize room prices
    const prices = {};
    reservation.rooms.forEach((room, i) => {
      prices[i] = room.pricePerNight || 0;
    });
    setRoomPrices(prices);
  };

  const openDetailsModal = (reservation) => {
    setActiveReservation(reservation);
    setShowDetailsModal(true);
  };

  const openConfirmModal = (reservation) => {
    setActiveReservation(reservation);
    setConfirmLanguage(reservation.preferredLanguage || "fr");
    setShowConfirmModal(true);
  };

  const openUnavailableModal = (reservation) => {
    setActiveReservation(reservation);
    setUnavailableLanguage(reservation.preferredLanguage || "fr");
    setUnavailableNotes("");
    setShowUnavailableModal(true);
  };

  const closeModals = () => {
    setShowQuoteModal(false);
    setShowDetailsModal(false);
    setShowPreviewModal(false);
    setShowConfirmModal(false);
    setShowUnavailableModal(false);
    setActiveReservation(null);
    setRoomPrices({});
    setTotalCost(0);
    setAdminNotes("");
    setPreviewContent({ subject: "", html: "", language: "" });
  };

  // Calculate total when room prices change
  useEffect(() => {
    if (!activeReservation) return;
    const nights = dateDiffInNights(activeReservation.checkIn, activeReservation.checkOut);
    const total = Object.values(roomPrices).reduce((sum, price) => sum + (price * nights), 0);
    setTotalCost(total);
  }, [roomPrices, activeReservation]);

  // Preview quote email
  const handlePreviewQuote = async () => {
    if (!activeReservation) return;
    
    try {
      const updatedRooms = activeReservation.rooms.map((room, i) => ({
        ...room,
        pricePerNight: roomPrices[i] || 0
      }));
      
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/preview-quote`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            rooms: updatedRooms,
            currency,
            totalCost,
            language,
            adminNotes
          }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de l'aperçu");
      
      const data = await response.json();
      setPreviewContent(data);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Preview confirmation email
  const handlePreviewConfirmation = async () => {
    if (!activeReservation) return;
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/preview-confirmation`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: confirmLanguage }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de l'aperçu");
      
      const data = await response.json();
      setPreviewContent(data);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Preview unavailable email
  const handlePreviewUnavailable = async () => {
    if (!activeReservation) return;
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/preview-unavailable`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: unavailableLanguage }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de l'aperçu");
      
      const data = await response.json();
      setPreviewContent(data);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Send quote
  const handleSendQuote = async () => {
    if (!activeReservation) return;
    if (!window.confirm("Envoyer le devis au client ?")) return;
    
    try {
      const updatedRooms = activeReservation.rooms.map((room, i) => ({
        ...room,
        pricePerNight: roomPrices[i] || 0
      }));
      
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/send-quote`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            rooms: updatedRooms,
            currency,
            totalCost,
            language,
            adminNotes
          }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de l'envoi du devis");
      
      alert("✅ Devis envoyé avec succès !");
      closeModals();
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message || "Échec de l'envoi du devis"}`);
    }
  };

  // Send unavailable
  const handleSendUnavailable = async () => {
    if (!activeReservation) return;
    if (!window.confirm("Envoyer la notification d'indisponibilité au client ?")) return;
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/send-unavailable`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            language: unavailableLanguage,
            adminNotes: unavailableNotes
          }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de l'envoi");
      
      alert("✅ Notification envoyée !");
      closeModals();
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Confirm reservation
  const handleConfirm = async () => {
    if (!activeReservation) return;
    if (!window.confirm("Confirmer cette réservation ?")) return;
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${activeReservation._id}/confirm`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ language: confirmLanguage }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de la confirmation");
      
      alert("✅ Réservation confirmée et email envoyé !");
      closeModals();
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Update status
  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservationId}/status`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: newStatus }),
        }
      );
      
      if (!response.ok) throw new Error("Échec de la mise à jour du statut");
      
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  // Delete reservation
  const handleDelete = async (reservationId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette réservation ?")) return;
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.RESERVATIONS}/${reservationId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );
      
      if (!response.ok) throw new Error("Échec de la suppression");
      
      alert("✅ Réservation supprimée");
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  const formatCurrency = (amount, curr) => {
    const symbols = { TND: 'TND', EUR: '€', USD: '$' };
    return `${symbols[curr] || curr} ${amount.toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#b59a60",
      quoted: "#5a7d9a",
      confirmed: "#4CAF50",
      "checked-in": "#8B4513",
      "checked-out": "#607D8B",
      cancelled: "#D32F2F",
      denied: "#757575",
    };
    return colors[status] || "#999";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "En attente",
      quoted: "Devis envoyé",
      confirmed: "Confirmée",
      "checked-in": "Arrivée",
      "checked-out": "Départ",
      cancelled: "Annulée",
      denied: "Refusée",
    };
    return labels[status] || status;
  };

  const getRoomTypeLabel = (roomType) => {
    const labels = {
      "economy-single": "Simple Économique",
      "superior-single": "Simple Supérieure",
      "economy-double": "Double Économique",
      "superior-double": "Double Supérieure",
      "triple": "Triple",
      "suite": "Suite",
    };
    return labels[roomType] || roomType;
  };

  // Filter reservations
  const filteredReservations = reservations.filter((resv) => {
    const matchesSearch = 
      resv.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resv.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resv.confirmationCode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Chargement des réservations...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Gestion des Réservations</h2>
        <button onClick={fetchReservations} className={styles.refreshBtn}>
          Actualiser
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <span>Erreur: {error}</span>
          <button onClick={fetchReservations} className={styles.retryBtn}>
            Réessayer
          </button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Statut</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="quoted">Devis envoyé</option>
            <option value="confirmed">Confirmée</option>
            <option value="checked-in">Arrivée</option>
            <option value="checked-out">Départ</option>
            <option value="cancelled">Annulée</option>
            <option value="denied">Refusée</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Rechercher</label>
          <input
            type="text"
            placeholder="Nom, email, code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Reservations Table */}
      {filteredReservations.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Aucune réservation trouvée</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Client</th>
                <th>Dates</th>
                <th>Chambres</th>
                <th>Coût Total</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservations.map((resv) => (
                <tr
                  key={resv._id}
                  className={styles.clickableRow}
                  onClick={() => navigate(`/dashboard/reservations/${resv._id}`)}
                >
                  <td>
                    <strong>{resv.confirmationCode}</strong>
                  </td>
                  <td>
                    <strong>{resv.firstName} {resv.lastName}</strong>
                    <br />
                    <small>{resv.email}</small>
                    <br />
                    <small>Langue: {resv.preferredLanguage?.toUpperCase() || "EN"}</small>
                  </td>
                  <td>
                    <strong>{new Date(resv.checkIn).toLocaleDateString('fr-FR')}</strong>
                    <br />
                    <small>au {new Date(resv.checkOut).toLocaleDateString('fr-FR')}</small>
                    <br />
                    <small>{dateDiffInNights(resv.checkIn, resv.checkOut)} nuit(s)</small>
                  </td>
                  <td>
                    {resv.rooms.map((room, i) => (
                      <div key={i}>
                        <small>{getRoomTypeLabel(room.roomType)}</small>
                        <br />
                        <small style={{ color: "#6b6560" }}>
                          {room.adults} adulte(s)
                          {room.childUnder2 > 0 && `, ${room.childUnder2} enfant(s) ${'<'}2`}
                          {room.child2to12 > 0 && `, ${room.child2to12} enfant(s) 2-12`}
                          {room.child12to18 > 0 && `, ${room.child12to18} enfant(s) 12-18`}
                        </small>
                      </div>
                    ))}
                  </td>
                  <td>
                    {resv.totalCost > 0 
                      ? formatCurrency(resv.totalCost, resv.currency || "TND")
                      : "Non défini"}
                  </td>
                  <td>
                    <span 
                      className={styles.statusBadge}
                      style={{ backgroundColor: getStatusColor(resv.status) }}
                    >
                      {getStatusLabel(resv.status)}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className={styles.actionButtons}>
                      <button
                        className={`${styles.btn} ${styles.info}`}
                        onClick={() => openDetailsModal(resv)}
                        title="Voir détails"
                      >
                        Détails
                      </button>
                      {resv.status === "pending" && (
                        <>
                          <button
                            className={`${styles.btn} ${styles.primary}`}
                            onClick={() => openQuoteModal(resv)}
                            title="Envoyer devis"
                          >
                            Devis
                          </button>
                          <button
                            className={`${styles.btn} ${styles.danger}`}
                            onClick={() => openUnavailableModal(resv)}
                            title="Indisponible"
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      {resv.status === "quoted" && (
                        <button
                          className={`${styles.btn} ${styles.success}`}
                          onClick={() => openConfirmModal(resv)}
                          title="Confirmer"
                        >
                          Confirmer
                        </button>
                      )}
                      <select
                        className={styles.statusSelect}
                        value={resv.status}
                        onChange={(e) => handleStatusChange(resv._id, e.target.value)}
                      >
                        <option value="pending">En attente</option>
                        <option value="quoted">Devis envoyé</option>
                        <option value="confirmed">Confirmée</option>
                        <option value="checked-in">Arrivée</option>
                        <option value="checked-out">Départ</option>
                        <option value="cancelled">Annulée</option>
                        <option value="denied">Refusée</option>
                      </select>
                      <button
                        className={`${styles.btn} ${styles.danger}`}
                        onClick={() => handleDelete(resv._id)}
                        title="Supprimer"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quote Modal */}
      {showQuoteModal && activeReservation && (
        <div className={styles.modalOverlay} onClick={closeModals}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Envoyer Devis à {activeReservation.firstName} {activeReservation.lastName}</h3>
              <button className={styles.closeBtn} onClick={closeModals}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              {/* Guest Info */}
              <div className={styles.infoSection}>
                <h4>Informations Client</h4>
                <p><strong>Email:</strong> {activeReservation.email}</p>
                <p><strong>Téléphone:</strong> {activeReservation.phone || "N/A"}</p>
                <p><strong>Code:</strong> {activeReservation.confirmationCode}</p>
              </div>

              {/* Stay Details */}
              <div className={styles.infoSection}>
                <h4>Détails du Séjour</h4>
                <p><strong>Arrivée:</strong> {new Date(activeReservation.checkIn).toLocaleDateString('fr-FR')}</p>
                <p><strong>Départ:</strong> {new Date(activeReservation.checkOut).toLocaleDateString('fr-FR')}</p>
                <p><strong>Nuits:</strong> {dateDiffInNights(activeReservation.checkIn, activeReservation.checkOut)}</p>
              </div>

              {/* Language & Currency */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Langue de l'email</label>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Devise</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    <option value="TND">Dinar Tunisien (TND)</option>
                    <option value="EUR">Euro (€)</option>
                    <option value="USD">Dollar US ($)</option>
                  </select>
                </div>
              </div>

              {/* Room Pricing */}
              <div className={styles.pricingSection}>
                <h4>Tarification par Chambre</h4>
                {activeReservation.rooms.map((room, index) => (
                  <div key={index} className={styles.roomPricing}>
                    <div className={styles.roomInfo}>
                      <strong>{getRoomTypeLabel(room.roomType)}</strong>
                      <small>
                        {room.adults} adulte(s)
                        {room.childUnder2 > 0 && `, ${room.childUnder2} ${'<'}2 ans`}
                        {room.child2to12 > 0 && `, ${room.child2to12} 2-12 ans`}
                        {room.child12to18 > 0 && `, ${room.child12to18} 12-18 ans`}
                      </small>
                    </div>
                    <input
                      type="number"
                      placeholder="Prix/nuit"
                      value={roomPrices[index] || ""}
                      onChange={(e) => setRoomPrices({ ...roomPrices, [index]: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                ))}
                
                <div className={styles.totalCost}>
                  Coût Total: <strong>{formatCurrency(totalCost, currency)}</strong>
                </div>
              </div>

              {/* Admin Notes */}
              <div className={styles.formGroup}>
                <label>Notes pour le client (optionnel)</label>
                <textarea
                  rows="3"
                  placeholder="Message personnalisé..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </div>

              {/* Special Requests */}
              {activeReservation.requests && (
                <div className={styles.infoSection}>
                  <h4>Demandes Spéciales du Client:</h4>
                  <p className={styles.requests}>{activeReservation.requests}</p>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className={`${styles.btn} ${styles.info}`} onClick={handlePreviewQuote}>
                👁️ Aperçu Email
              </button>
              <button className={`${styles.btn} ${styles.success}`} onClick={handleSendQuote}>
                ✉️ Envoyer Devis
              </button>
              <button className={`${styles.btn} ${styles.secondary}`} onClick={closeModals}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && activeReservation && (
        <div className={styles.modalOverlay} onClick={closeModals}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Confirmer Réservation</h3>
              <button className={styles.closeBtn} onClick={closeModals}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.infoSection}>
                <h4>Client</h4>
                <p><strong>{activeReservation.firstName} {activeReservation.lastName}</strong></p>
                <p>{activeReservation.email}</p>
                <p>Code: {activeReservation.confirmationCode}</p>
              </div>

              <div className={styles.formGroup}>
                <label>Langue de l'email de confirmation</label>
                <select value={confirmLanguage} onChange={(e) => setConfirmLanguage(e.target.value)}>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={`${styles.btn} ${styles.info}`} onClick={handlePreviewConfirmation}>
                👁️ Aperçu Email
              </button>
              <button className={`${styles.btn} ${styles.success}`} onClick={handleConfirm}>
                ✉️ Confirmer & Envoyer
              </button>
              <button className={`${styles.btn} ${styles.secondary}`} onClick={closeModals}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unavailable Modal */}
      {showUnavailableModal && activeReservation && (
        <div className={styles.modalOverlay} onClick={closeModals}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Notifier Indisponibilité</h3>
              <button className={styles.closeBtn} onClick={closeModals}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.infoSection}>
                <h4>Client</h4>
                <p><strong>{activeReservation.firstName} {activeReservation.lastName}</strong></p>
                <p>{activeReservation.email}</p>
              </div>

              <div className={styles.formGroup}>
                <label>Langue de l'email</label>
                <select value={unavailableLanguage} onChange={(e) => setUnavailableLanguage(e.target.value)}>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Message pour le client (optionnel)</label>
                <textarea
                  rows="4"
                  placeholder="Dates alternatives, suggestions..."
                  value={unavailableNotes}
                  onChange={(e) => setUnavailableNotes(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={`${styles.btn} ${styles.info}`} onClick={handlePreviewUnavailable}>
                👁️ Aperçu Email
              </button>
              <button className={`${styles.btn} ${styles.danger}`} onClick={handleSendUnavailable}>
                ✉️ Envoyer Notification
              </button>
              <button className={`${styles.btn} ${styles.secondary}`} onClick={closeModals}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreviewModal && (
        <div className={styles.modalOverlay} onClick={() => setShowPreviewModal(false)}>
          <div className={styles.modalContent} style={{ maxWidth: "800px" }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Aperçu de l'Email ({previewContent.language?.toUpperCase()})</h3>
              <button className={styles.closeBtn} onClick={() => setShowPreviewModal(false)}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.infoSection}>
                <h4>Sujet</h4>
                <p><strong>{previewContent.subject}</strong></p>
              </div>

              <div className={styles.infoSection}>
                <h4>Contenu HTML</h4>
                <div 
                  style={{ 
                    border: "1px solid #e8e6e3", 
                    padding: "20px", 
                    background: "#fff",
                    maxHeight: "500px",
                    overflow: "auto"
                  }}
                  dangerouslySetInnerHTML={{ __html: previewContent.html }}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={`${styles.btn} ${styles.secondary}`} onClick={() => setShowPreviewModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && activeReservation && (
        <div className={styles.modalOverlay} onClick={closeModals}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Détails de la Réservation</h3>
              <button className={styles.closeBtn} onClick={closeModals}>×</button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <strong>Code</strong>
                  <span>{activeReservation.confirmationCode}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Statut</strong>
                  <span style={{ color: getStatusColor(activeReservation.status) }}>
                    {getStatusLabel(activeReservation.status)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Nom</strong>
                  <span>{activeReservation.firstName} {activeReservation.lastName}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Email</strong>
                  <span>{activeReservation.email}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Téléphone</strong>
                  <span>{activeReservation.phone || "N/A"}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Langue</strong>
                  <span>{activeReservation.preferredLanguage?.toUpperCase() || "EN"}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Arrivée</strong>
                  <span>{new Date(activeReservation.checkIn).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Départ</strong>
                  <span>{new Date(activeReservation.checkOut).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Nuits</strong>
                  <span>{dateDiffInNights(activeReservation.checkIn, activeReservation.checkOut)}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Devise</strong>
                  <span>{activeReservation.currency || "TND"}</span>
                </div>
                <div className={styles.detailItem}>
                  <strong>Coût Total</strong>
                  <span>
                    {activeReservation.totalCost > 0 
                      ? formatCurrency(activeReservation.totalCost, activeReservation.currency || "TND")
                      : "Non défini"}
                  </span>
                </div>
              </div>

              <div className={styles.infoSection}>
                <h4>Chambres</h4>
                {activeReservation.rooms.map((room, i) => (
                  <div key={i} className={styles.roomDetail}>
                    <h5>{getRoomTypeLabel(room.roomType)}</h5>
                    <p>Adultes: {room.adults}</p>
                    {room.childUnder2 > 0 && <p>Enfants {'<'}2 ans: {room.childUnder2}</p>}
                    {room.child2to12 > 0 && <p>Enfants 2-12 ans: {room.child2to12}</p>}
                    {room.child12to18 > 0 && <p>Enfants 12-18 ans: {room.child12to18}</p>}
                    {room.pricePerNight > 0 && (
                      <p><strong>Prix/nuit: {formatCurrency(room.pricePerNight, activeReservation.currency || "TND")}</strong></p>
                    )}
                  </div>
                ))}
              </div>

              {activeReservation.requests && (
                <div className={styles.infoSection}>
                  <h4>Demandes Spéciales</h4>
                  <p className={styles.requests}>{activeReservation.requests}</p>
                </div>
              )}

              {activeReservation.adminNotes && (
                <div className={styles.infoSection}>
                  <h4>Notes Admin</h4>
                  <p className={styles.adminNotes}>{activeReservation.adminNotes}</p>
                </div>
              )}

              {activeReservation.emailsSent && activeReservation.emailsSent.length > 0 && (
                <div className={styles.infoSection}>
                  <h4>Historique des Emails</h4>
                  <ul className={styles.emailHistory}>
                    {activeReservation.emailsSent.map((email, i) => (
                      <li key={i}>
                        {email.type} ({email.language?.toUpperCase()}) - {new Date(email.sentAt).toLocaleString('fr-FR')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={styles.timestamps}>
                <span>Créée: {new Date(activeReservation.createdAt).toLocaleString('fr-FR')}</span>
                <span>Modifiée: {new Date(activeReservation.updatedAt).toLocaleString('fr-FR')}</span>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={`${styles.btn} ${styles.secondary}`} onClick={closeModals}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReservationsEnhanced;
